import { useLayoutEffect, useMemo, useRef } from 'react';

/**
 * useKeepChatScrollOnViewChange
 *
 * Keeps the chat viewport in place when the user toggles the translation view on/off.
 *
 * Problem: toggling the translation view swaps each message body (original <-> translation),
 * so message heights change and the scroll position jumps, losing the reading spot.
 *
 * Approach (all logic centralized here instead of being spread across Chat.js / ChatKnowledge.js):
 *   1. On every user scroll, snapshot which message sits at the top of the viewport
 *      and its distance from the viewport top.
 *   2. When the translation view changes and the layout re-renders, use the snapshot to
 *      pull the scroll back (keep that message at the same relative position on screen).
 *   3. Translation/markdown/images may render one frame late, so restore once more on the
 *      next frame to absorb height changes that land after paint.
 *
 * @param {Object} params
 * @param {string} params.panelId          - panel id (used to build the chat container id).
 * @param {string} params.chatThreadId     - id of the open thread.
 * @param {Object} params.chatPostOnDemand - per-thread/per-message translation view state (Recoil).
 * @param {string} [params.focusedMsgId]   - message currently being focused (if any).
 * @param {boolean} [params.triggerFocusMsg] - flag signaling a focus-to-message is in progress.
 * @returns {{ onChatScroll: Function }} - attach onChatScroll to the chat container's onScroll.
 */
const useKeepChatScrollOnViewChange = ({
    panelId,
    chatThreadId,
    chatPostOnDemand,
    focusedMsgId,
    triggerFocusMsg
}) => {
    // Id of the scroll container holding the message list.
    const chatContentListId = `divChatContentList-${panelId}-${chatThreadId}`;

    // Snapshot of the scroll position captured on the latest user scroll.
    const scrollAnchorRef = useRef(null);

    // Flag indicating an automatic restore is in progress, so onScroll does not
    // overwrite the snapshot with an intermediate position produced by our own code.
    const isRestoringRef = useRef(false);

    /**
     * Step 1: Snapshot the current scroll position.
     * - If near the bottom: just remember "at bottom" so we stick to the bottom later.
     * - Otherwise: pick the first message in the viewport as the anchor and remember its
     *   id plus its distance from the viewport top (relative offset).
     */
    const captureScrollAnchor = () => {
        const container = document.getElementById(chatContentListId);
        if (!container) return;

        // Treat as "at bottom" when within 100px of the bottom.
        const isAtBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight <= 100;

        if (isAtBottom) {
            scrollAnchorRef.current = { isAtBottom: true, anchorId: null, offsetInViewport: 0 };
            return;
        }

        // Find the message sitting at the top of the viewport to use as the anchor.
        const items = container.querySelectorAll('.chatBox-text[id]');
        const viewportTop = container.scrollTop;
        let anchor = null;
        for (const el of items) {
            // The first message whose top edge is >= the viewport top is the anchor.
            if (el.offsetTop >= viewportTop - 1) {
                anchor = el;
                break;
            }
            anchor = el;
        }
        if (!anchor) {
            scrollAnchorRef.current = null;
            return;
        }

        scrollAnchorRef.current = {
            isAtBottom: false,
            anchorId: anchor.id,
            // Distance from the message top to the viewport top (kept constant on restore).
            offsetInViewport: anchor.offsetTop - viewportTop
        };
    };

    /**
     * Step 2: Restore the scroll position from the captured snapshot.
     * - If it was at the bottom: scroll back to the bottom.
     * - If there is an anchor: set scrollTop so the anchor returns to its old relative spot.
     */
    const restoreScrollAnchor = () => {
        const snapshot = scrollAnchorRef.current;
        if (!snapshot) return;

        const container = document.getElementById(chatContentListId);
        if (!container) return;

        if (snapshot.isAtBottom) {
            container.scrollTop = container.scrollHeight;
            return;
        }

        if (!snapshot.anchorId) return;
        const anchorEl = document.getElementById(snapshot.anchorId);
        if (!anchorEl) return;

        // Reset scroll so the anchor message returns to its old distance from the viewport top.
        container.scrollTop = anchorEl.offsetTop - snapshot.offsetInViewport;
    };

    /**
     * Signature that changes whenever any thread/message translation view flips on/off.
     * Used as the dependency that triggers the scroll-keeping effect below.
     */
    const translationViewSignature = useMemo(() => {
        const parts = [];
        for (const tid in chatPostOnDemand) {
            const record = chatPostOnDemand[tid];
            if (!record) continue;
            parts.push(`${tid}:${record.activeView}`);
            const messages = record.messages || {};
            for (const pid in messages) {
                parts.push(`${pid}:${messages[pid]?.activeView}`);
            }
        }
        return parts.join('|');
    }, [chatPostOnDemand]);

    /**
     * Step 3: When the translation view changes, keep the scroll in place (runs before paint).
     * - Skip while focusing a specific message, to avoid fighting the focus scroll.
     * - Restore immediately (absorb the height change), then restore once more on the next
     *   frame to absorb late-rendering content (images/markdown/translation arriving late).
     */
    useLayoutEffect(() => {
        if (focusedMsgId && triggerFocusMsg) {
            // Focusing a message: only snapshot the position, do not pull the scroll.
            captureScrollAnchor();
            return;
        }

        // Set the flag so onScroll fired by our restore does not overwrite the snapshot.
        isRestoringRef.current = true;

        // Restore #1: right before paint.
        restoreScrollAnchor();

        // Restore #2: after the layout settles (absorbs late-rendering content).
        // Do NOT re-capture the anchor here: keep the user's reading anchor fixed across
        // the stream of translation batches (auto-translate applies group by group).
        // The anchor only updates on a real user scroll (via onChatScroll).
        const rafId = requestAnimationFrame(() => {
            restoreScrollAnchor();
            isRestoringRef.current = false;
        });

        return () => cancelAnimationFrame(rafId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translationViewSignature]);

    /**
     * Attach to the chat container's onScroll.
     * Only snapshot on a real user scroll; skip while our code is restoring.
     */
    const onChatScroll = () => {
        if (isRestoringRef.current) return;
        captureScrollAnchor();
    };

    return { onChatScroll };
};

export default useKeepChatScrollOnViewChange;
